import type { PayLinkRowResponse } from "../services/payLinkService";
import type { BusinessTransactionRow } from "./businessSuiteTransactionRowMap";
import { mapApiPayLinkToRow } from "./mapApiPayLinkToRow";
import { formatCurrencyValue } from "./numberFormatter";

const PAID_PAYLINK_STATUSES = new Set(["paid", "confirmed", "completed"]);

function parsePayLinkDate(value: string | undefined): Date | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateLine(date: Date | null): string {
  if (!date) {
    return "—";
  }

  return date.toLocaleDateString("en-ZA", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatTimeLine(date: Date | null): string {
  if (!date) {
    return "";
  }

  return date.toLocaleTimeString("en-ZA", {
    timeStyle: "medium",
  });
}

function formatRowTime(date: Date | null): string {
  if (!date) {
    return "";
  }

  return date.toLocaleString("en-ZA", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function normalizeStatus(value: string | undefined): string {
  return String(value || "")
    .trim()
    .toLowerCase();
}

export function isPaidBusinessPayLink(row: PayLinkRowResponse): boolean {
  return PAID_PAYLINK_STATUSES.has(normalizeStatus(row.status));
}

export function getBusinessPayLinksTotalPaidInZar(
  rows: PayLinkRowResponse[] | undefined
): number {
  return (rows ?? []).reduce((total, row) => {
    if (!isPaidBusinessPayLink(row)) {
      return total;
    }

    return total + (mapApiPayLinkToRow(row).amountNumeric ?? 0);
  }, 0);
}

export function mapPaidPayLinkToBusinessTransactionRow(
  row: PayLinkRowResponse
): BusinessTransactionRow | null {
  if (!isPaidBusinessPayLink(row)) {
    return null;
  }

  const mapped = mapApiPayLinkToRow(row);
  const amountNumeric = mapped.amountNumeric ?? 0;
  const createdAt = parsePayLinkDate(row.updatedAt ?? row.createdAt);
  const id = `paylink-${mapped.id}`;
  const amountFormatted =
    mapped.amountFormatted || formatCurrencyValue(amountNumeric, "ZAR");
  const reference = mapped.reference || "—";
  const transactionId = row.payLinkId || row._id || mapped.id;

  return {
    id,
    direction: "received",
    actionLabel: "PayLink payment",
    actionLabelMobile: "PayLink",
    time: formatRowTime(createdAt),
    primaryAmount: amountFormatted,
    secondaryAmount: reference,
    status: "Complete",
    sortAmount: amountNumeric,
    sortDate: createdAt?.getTime() ?? 0,
    filterType: "paylink",
    filterStatus: "complete",
    detail: {
      heroZarAmount: amountFormatted,
      listAmount: amountFormatted,
      transactionType: "PayLink",
      statusLabel: "COMPLETE",
      statusTone: "complete",
      action: "Receive",
      transactionMethod: mapped.paymentMethod === "crypto" ? "Crypto" : "88Pay",
      transactionId,
      dateLine: formatDateLine(createdAt),
      timeLine: formatTimeLine(createdAt),
      serviceCharge: "R 0.00",
      totalCost: amountFormatted,
      showSplitPayment: false,
    },
  };
}

export function mapPaidPayLinksToBusinessTransactionRows(
  rows: PayLinkRowResponse[] | undefined
): BusinessTransactionRow[] {
  return (rows ?? [])
    .map(mapPaidPayLinkToBusinessTransactionRow)
    .filter((row): row is BusinessTransactionRow => row !== null);
}
